import { useMemo } from 'react';
import { useDataFetch } from './Use-data-fetch';
import { formatDateDayMonthYear } from '../utils/date';

/**
 * Normaliza el bloque de un periodo cerrado anterior que devuelve el caché.
 * Viene de la última liquidación guardada: la semana pasada para bonos y el
 * mes pasado para regalías.
 */
const mapPreviousPeriod = (previous) => {
  if (!previous) return null;

  return {
    settlementId: previous.settlement_id,
    totalSettlementByCurrency: previous.totalSettlementByCurrency || {},
    calculationDate: previous.calculation_date,
    status: previous.status,
    paidStatus: previous.paid_status,
    period: {
      start: previous.period?.start,
      end: previous.period?.end,
      startFormatted: formatDateDayMonthYear(previous.period?.start),
      endFormatted: formatDateDayMonthYear(previous.period?.end),
    },
  };
};

/**
 * Hook personalizado para obtener y manejar los datos de settlements (bonos y regalías)
 * Usa el caché precalculado cada hora cuando el periodo es 'current',
 * y los endpoints individuales para periodos históricos concretos.
 * @returns {Object} Datos y funciones relacionadas con settlements
 */
export const useSettlementData = (settlementId, loyaltySettlementId, currency) => {
  const { getData, postData, invalidateQuery } = useDataFetch();

  const useCacheForInfluencer = settlementId === 'current';
  const useCacheForLoyalty = loyaltySettlementId === 'current';
  const useCombinedCache = useCacheForInfluencer && useCacheForLoyalty;

  // Endpoint de caché combinado (ambos periodos son 'current')
  const {
    data: cacheData,
    isLoading: cacheLoading,
    error: cacheError,
    refetch: refetchCache,
  } = getData(
    ['dashboard-cache', currency],
    `/charts/dashboard-cache`,
    // El caché se regenera cada hora en settlement-ms: se vuelve a pedir cada
    // 5 minutos y al volver a la pestaña para mostrarlo sin recargar la página.
    {
      enabled: useCombinedCache && !!currency,
      refetchInterval: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
    },
    { currency }
  );

  // Endpoint individual de bonos de influencer (solo para periodos históricos)
  const {
    data: influencerBonusData,
    isLoading: influencerHistoricLoading,
    error: influencerHistoricError,
    refetch: refetchInfluencer,
  } = getData(
    ['influencer-bonus', settlementId, currency],
    `/charts/influencer-bonus`,
    { enabled: !useCacheForInfluencer && !!settlementId && !!currency },
    { id: settlementId, currency }
  );

  // Endpoint individual de regalías de lealtad (solo para periodos históricos)
  const {
    data: loyaltyRoyaltyData,
    isLoading: loyaltyHistoricLoading,
    error: loyaltyHistoricError,
    refetch: refetchLoyalty,
  } = getData(
    ['loyalty-royalty', loyaltySettlementId, currency],
    `/charts/loyalty-royalty`,
    { enabled: !useCacheForLoyalty && !!loyaltySettlementId && !!currency },
    { id: loyaltySettlementId, currency }
  );

  // Datos procesados de bonos de influencer
  const influencerData = useMemo(() => {
    if (useCacheForInfluencer) {
      const cached = cacheData?.data?.influencer;
      if (!cached) return null;
      return {
        totalSettlementByCurrency: cached.totalSettlementByCurrency || {},
        period: {
          start: cached.period?.start,
          end: cached.period?.end,
          startFormatted: formatDateDayMonthYear(cached.period?.start),
          endFormatted: formatDateDayMonthYear(cached.period?.end),
        },
        incomeData: cached.incomeChartData || {},
        // Periodo cerrado anterior y variación contra lo que va corriendo.
        previous: mapPreviousPeriod(cached.previous),
        growth: cached.growth || {},
        levelsSummary: cached.levels_summary || [],
        usersCount: cached.users_count || 0,
        generated_at: cached.generated_at,
        generated_at_formatted: cached.generated_at
          ? new Date(cached.generated_at).toLocaleString('es-CO', { timeZone: 'America/Bogota' })
          : null,
      };
    }

    if (!influencerBonusData?.data?.bonusData) return null;
    const { bonusData, incomeChartData } = influencerBonusData.data;
    return {
      totalSettlementByCurrency: bonusData.totalSettlementByCurrency || {},
      period: {
        start: bonusData.period?.start,
        end: bonusData.period?.end,
        startFormatted: formatDateDayMonthYear(bonusData.period?.start),
        endFormatted: formatDateDayMonthYear(bonusData.period?.end),
      },
      incomeData: incomeChartData || {},
      generated_at: null,
      generated_at_formatted: null,
    };
  }, [useCacheForInfluencer, cacheData, influencerBonusData]);

  // Datos procesados de regalías de lealtad
  const loyaltyData = useMemo(() => {
    if (useCacheForLoyalty) {
      const cached = cacheData?.data?.loyalty;
      if (!cached) return null;
      return {
        totalSettlementByCurrency: cached.totalSettlementByCurrency || {},
        period: {
          start: cached.period?.start,
          end: cached.period?.end,
          startFormatted: formatDateDayMonthYear(cached.period?.start),
          endFormatted: formatDateDayMonthYear(cached.period?.end),
        },
        incomeData: cached.incomeChartData || {},
        previous: mapPreviousPeriod(cached.previous),
        growth: cached.growth || {},
        levelsSummary: cached.levels_summary || [],
        usersCount: cached.users_count || 0,
        generated_at: cached.generated_at,
        generated_at_formatted: cached.generated_at
          ? new Date(cached.generated_at).toLocaleString('es-CO', { timeZone: 'America/Bogota' })
          : null,
      };
    }

    if (!loyaltyRoyaltyData?.data?.royaltyData) return null;
    const { royaltyData, incomeChartData } = loyaltyRoyaltyData.data;
    return {
      totalSettlementByCurrency: royaltyData.totalSettlementByCurrency || {},
      period: {
        start: royaltyData.period?.start,
        end: royaltyData.period?.end,
        startFormatted: formatDateDayMonthYear(royaltyData.period?.start),
        endFormatted: formatDateDayMonthYear(royaltyData.period?.end),
      },
      incomeData: incomeChartData || {},
      generated_at: null,
      generated_at_formatted: null,
    };
  }, [useCacheForLoyalty, cacheData, loyaltyRoyaltyData]);

  const influencerLoading = useCacheForInfluencer ? cacheLoading : influencerHistoricLoading;
  const loyaltyLoading = useCacheForLoyalty ? cacheLoading : loyaltyHistoricLoading;
  const influencerError = useCacheForInfluencer ? cacheError : influencerHistoricError;
  const loyaltyError = useCacheForLoyalty ? cacheError : loyaltyHistoricError;

  const refetchAll = () => {
    if (useCombinedCache) {
      refetchCache();
    } else {
      if (!useCacheForInfluencer) refetchInfluencer();
      if (!useCacheForLoyalty) refetchLoyalty();
      if (useCombinedCache) refetchCache();
    }
  };

  // Regeneración manual del caché.
  //
  // El caché lo recalcula un cron cada hora; esto fuerza el recálculo de
  // inmediato. Es lo que hay que llamar después de un despliegue que cambie la
  // forma del caché, porque el documento guardado conserva la forma anterior
  // hasta que se regenere.
  const refreshCacheMutation = postData('/charts/refresh-dashboard-cache', {}, {
    onSuccess: () => {
      invalidateQuery(['dashboard-cache']);
      refetchCache();
    },
  });

  const refreshCache = () => refreshCacheMutation.mutate({});

  return {
    influencerData,
    loyaltyData,
    influencerLoading,
    loyaltyLoading,
    isLoading: influencerLoading || loyaltyLoading,
    influencerError,
    loyaltyError,
    hasError: influencerError || loyaltyError,
    refetchInfluencer,
    refetchLoyalty,
    refetchAll,
    refreshCache,
    isRefreshingCache: refreshCacheMutation.isPending,
    refreshCacheError: refreshCacheMutation.error,
  };
};

/**
 * Detalle de un nivel del caché: las órdenes y los usuarios que generaron sus
 * puntos. Se pide sólo cuando el usuario abre un nivel, porque el backend
 * hidrata las órdenes en esa llamada.
 *
 * @param type 'influencer' | 'loyalty'
 * @param level nivel a consultar; null cierra la consulta
 */
export const useSettlementLevelDetail = (type, level, currency) => {
  const { getData } = useDataFetch();

  const { data, isLoading, error } = getData(
    ['dashboard-cache-level', type, level, currency],
    '/charts/dashboard-cache',
    { enabled: !!level && !!currency },
    { level, currency }
  );

  const detail = useMemo(() => {
    if (!level) return null;
    return data?.data?.[type]?.level_detail || null;
  }, [data, type, level]);

  return { detail, isLoading, error };
};
